import { useQuery } from "@tanstack/react-query";
import { FileService } from "services/Files";
import { isReactSnapPrerender } from "services/utils";

export const PRICING_CONTEXT_FILE_PATH = "pricing-context.json";
export const PRICING_CONTEXT_QUERY_KEY = ["pricing-context-file"];

const USD_FALLBACK_BLOCK = Object.freeze({
  currency: "USD",
  monthly: {
    amount_minor: 500,
    currency_symbol: "$",
    price_id: "",
  },
  yearly: {
    amount_minor: 3900,
    currency_symbol: "$",
    price_id: "",
  },
});

export const HARD_FALLBACK_PRICING_CONTEXT = Object.freeze({
  default_currency: "USD",
  country_to_currency: {},
  prices_by_currency: {
    USD: USD_FALLBACK_BLOCK,
  },
  version: "fallback",
});

const normalizeCountryCode = (value) => {
  if (typeof value !== "string") {
    return "";
  }

  const normalizedCountryCode = value.toUpperCase().trim();
  return /^[A-Z]{2}$/.test(normalizedCountryCode) ? normalizedCountryCode : "";
};

const normalizeCurrencyCode = (value) => {
  if (typeof value !== "string") {
    return "";
  }

  const normalizedCurrencyCode = value.toUpperCase().trim();
  return /^[A-Z]{3}$/.test(normalizedCurrencyCode) ? normalizedCurrencyCode : "";
};

const getPricingContextOrFallback = (pricingContext) => (
  pricingContext && typeof pricingContext === "object"
    ? pricingContext
    : HARD_FALLBACK_PRICING_CONTEXT
);

const resolveCurrencySymbol = (rawSymbol, currencyCode) => {
  if (typeof rawSymbol === "string" && rawSymbol.trim() !== "") {
    return rawSymbol.trim();
  }

  const normalizedCurrencyCode = normalizeCurrencyCode(currencyCode);
  if (!normalizedCurrencyCode) {
    return USD_FALLBACK_BLOCK.monthly.currency_symbol;
  }

  try {
    const formatter = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: normalizedCurrencyCode,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    const currencyPart = formatter.formatToParts(0).find((part) => part.type === "currency");
    if (typeof currencyPart?.value === "string" && currencyPart.value.trim() !== "") {
      return currencyPart.value.trim();
    }
  } catch {
    // Fallback to USD symbol when Intl cannot resolve a currency symbol.
  }

  return USD_FALLBACK_BLOCK.monthly.currency_symbol;
};

export const resolveEffectiveCountry = ({ userCountry, selectedCountry, detectedCountry } = {}) => {
  const normalizedUserCountry = normalizeCountryCode(userCountry);
  if (normalizedUserCountry) {
    return normalizedUserCountry;
  }

  const normalizedSelectedCountry = normalizeCountryCode(selectedCountry);
  if (normalizedSelectedCountry) {
    return normalizedSelectedCountry;
  }

  const normalizedDetectedCountry = normalizeCountryCode(detectedCountry);
  if (normalizedDetectedCountry) {
    return normalizedDetectedCountry;
  }

  return "US";
};

export const resolveEffectiveCurrency = (effectiveCountry, pricingContext) => {
  const context = getPricingContextOrFallback(pricingContext);
  const countryCode = normalizeCountryCode(effectiveCountry);
  const countryToCurrency = context?.country_to_currency && typeof context.country_to_currency === "object"
    ? context.country_to_currency
    : {};

  const mappedCurrency = normalizeCurrencyCode(countryToCurrency[countryCode]);
  if (mappedCurrency) {
    return { currency: mappedCurrency, usedFallback: false };
  }

  const defaultCurrency = normalizeCurrencyCode(context?.default_currency);
  if (defaultCurrency) {
    return { currency: defaultCurrency, usedFallback: true };
  }

  return { currency: "USD", usedFallback: true };
};

const normalizePricingBlock = (pricingBlock, fallbackCurrency) => {
  if (!pricingBlock || typeof pricingBlock !== "object") {
    return null;
  }

  const monthly = pricingBlock.monthly;
  const yearly = pricingBlock.yearly;
  if (!monthly || typeof monthly !== "object" || !yearly || typeof yearly !== "object") {
    return null;
  }

  const currency = normalizeCurrencyCode(pricingBlock.currency) || normalizeCurrencyCode(fallbackCurrency) || "USD";
  const fallbackCurrencySymbol = resolveCurrencySymbol("", currency);

  return {
    currency,
    monthly: {
      amount_minor: Number.isFinite(monthly.amount_minor) ? monthly.amount_minor : USD_FALLBACK_BLOCK.monthly.amount_minor,
      currency_symbol: resolveCurrencySymbol(monthly.currency_symbol, currency) || fallbackCurrencySymbol,
      price_id: typeof monthly.price_id === "string" ? monthly.price_id : "",
    },
    yearly: {
      amount_minor: Number.isFinite(yearly.amount_minor) ? yearly.amount_minor : USD_FALLBACK_BLOCK.yearly.amount_minor,
      currency_symbol: resolveCurrencySymbol(yearly.currency_symbol, currency) || fallbackCurrencySymbol,
      price_id: typeof yearly.price_id === "string" ? yearly.price_id : "",
    },
  };
};

const formatPriceAmount = (amountMinor) => {
  if (!Number.isFinite(amountMinor)) {
    return "";
  }

  const amountMajor = amountMinor / 100;
  const hasFractionalPart = Math.abs(amountMajor % 1) > Number.EPSILON;
  const numberFormatter = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: hasFractionalPart ? 2 : 0,
    maximumFractionDigits: 2,
  });
  const formattedNumber = numberFormatter.format(amountMajor);
  return formattedNumber;
};

export const getPricingForCurrency = (effectiveCurrency, pricingContext) => {
  const context = getPricingContextOrFallback(pricingContext);
  const pricesByCurrency = context?.prices_by_currency && typeof context.prices_by_currency === "object"
    ? context.prices_by_currency
    : {};

  const normalizedCurrency = normalizeCurrencyCode(effectiveCurrency);
  const directBlock = normalizePricingBlock(pricesByCurrency[normalizedCurrency], normalizedCurrency);
  if (directBlock) {
    return { pricing: directBlock, usedFallback: false };
  }

  const defaultCurrency = normalizeCurrencyCode(context?.default_currency) || "USD";
  const defaultBlock = normalizePricingBlock(pricesByCurrency[defaultCurrency], defaultCurrency);
  if (defaultBlock) {
    return { pricing: defaultBlock, usedFallback: true };
  }

  return {
    pricing: USD_FALLBACK_BLOCK,
    usedFallback: true,
  };
};

export const buildPlanData = (pricingBlock) => {
  const normalizedBlock = normalizePricingBlock(pricingBlock, "USD") || USD_FALLBACK_BLOCK;

  return {
    monthly: {
      price: formatPriceAmount(normalizedBlock.monthly.amount_minor),
      currencySymbol: normalizedBlock.monthly.currency_symbol,
      amountMinor: normalizedBlock.monthly.amount_minor,
      currency: normalizedBlock.currency,
      priceId: normalizedBlock.monthly.price_id,
    },
    yearly: {
      price: formatPriceAmount(normalizedBlock.yearly.amount_minor),
      currencySymbol: normalizedBlock.yearly.currency_symbol,
      amountMinor: normalizedBlock.yearly.amount_minor,
      currency: normalizedBlock.currency,
      priceId: normalizedBlock.yearly.price_id,
    },
  };
};

export const usePricingContextFile = () => {
  const isPrerender = isReactSnapPrerender();

  return useQuery({
    queryKey: PRICING_CONTEXT_QUERY_KEY,
    enabled: !isPrerender,
    queryFn: () => FileService.getPublicFileContent(PRICING_CONTEXT_FILE_PATH),
    staleTime: 1000 * 60 * 60 * 6,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
};
